import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import logo from "@/assets/logo.webp";
import { ArrowLeft, Mail } from "lucide-react";
import DevAvatarAnimation from "@/components/auth/DevAvatarAnimation";

const ForgotPassword = () => {
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const trimmed = identifier.trim();
      if (trimmed.includes("@")) {
        const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (error) throw error;
      } else {
        // Treat as Form IV Index — server-side lookup
        const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
        await fetch(
          `https://${projectId}.supabase.co/functions/v1/lookup-student-email-by-index`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ identifier: trimmed, redirect_origin: window.location.origin }),
          }
        );
      }
      setSent(true);
      toast({ title: "Request received", description: "If an account exists, a reset link has been sent." });
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 gap-6">
      <div className="flex flex-col items-center gap-2">
        <div className="h-[104px] w-[104px] rounded-full bg-primary/10 flex items-center justify-center p-2">
          <img src={logo} alt="St. David College" className="h-full w-full object-contain" />
        </div>
        <h1 className="text-xl font-bold text-foreground">St. David College of Health Sciences</h1>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-lg">Reset your password</CardTitle>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <p className="text-center text-sm text-muted-foreground">
                If an account matches what you entered, we've sent a password reset link to the email
                on file. Please check your inbox (and spam folder).
              </p>
              <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter your email <strong>or</strong> your Form IV Index (e.g. <code className="text-xs">S3424/0025/2023</code>)
                and we'll send a reset link to the email on file.
              </p>
              <div className="space-y-2">
                <Label htmlFor="identifier">Email or Form IV Index</Label>
                <Input
                  id="identifier"
                  type="text"
                  autoComplete="off"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="you@example.com  or  S3424/0025/2023"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
              <Button variant="ghost" className="w-full" type="button" onClick={() => navigate("/login")}>
                <ArrowLeft className="mr-2 h-4 w-4" /> Back to Login
              </Button>
            </form>
          )}
          <div className="flex justify-center pt-4">
            <DevAvatarAnimation />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
